import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    FormControl,
    FormControlLabel,
    IconButton,
    LinearProgress,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Swal from 'sweetalert2';
import moment from 'moment';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import { MessageTemplateDetail } from '../../models/messageTemplateTypes';
import {
    applySampleMergeValues,
    validateMessageTemplateForm,
} from '../../utils/messageTemplateValidation';
import { MessageTemplateBodyPreview } from './MessageTemplateBodyPreview';
import {
    bodyTextareaSx,
    cancelButtonSx,
    editDialogBodySx,
    editDialogFooterSx,
    editDialogHeaderIconSx,
    editDialogHeaderSx,
    editDialogPaperSx,
    fieldLabelMutedSx,
    fieldLabelSx,
    inputSx,
    previewBoxSx,
    readOnlyInputSx,
    saveButtonSx,
    sectionLabelSx,
    sectionSx,
    varChipSx,
} from './messageTemplateEditDialogStyles';

interface Props {
    open: boolean;
    source: string | null;
    id: number | null;
    onClose: () => void;
    onSaved: () => void;
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography component="div" sx={sectionLabelSx}>
        {children}
    </Typography>
);

const FieldLabel: React.FC<{ htmlFor?: string; children: React.ReactNode; readOnly?: boolean }> = ({
    htmlFor,
    children,
    readOnly,
}) => (
    <Typography component="label" htmlFor={htmlFor} sx={fieldLabelSx}>
        {children}
        {readOnly && (
            <Box component="span" sx={fieldLabelMutedSx}>
                {' '}
                (read-only)
            </Box>
        )}
    </Typography>
);

export const EditMessageTemplateDialog: React.FC<Props> = ({
    open,
    source,
    id,
    onClose,
    onSaved,
}) => {
    const { user } = useAuth();
    const token = user?.token;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [template, setTemplate] = useState<MessageTemplateDetail | null>(null);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [active, setActive] = useState(true);
    const [adminConfirmed, setAdminConfirmed] = useState(false);
    const [originalBody, setOriginalBody] = useState('');

    useEffect(() => {
        if (!open || !source || !id || !token) {
            setTemplate(null);
            setLoadError(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        apiClient(`/MessageTemplates/detail?source=${encodeURIComponent(source)}&id=${id}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
        })
            .then((res) => {
                if (cancelled) return;
                if (!res?.status || !res.data?.template) {
                    setLoadError(res?.message || 'Failed to load template.');
                    return;
                }
                const t = res.data.template as MessageTemplateDetail;
                setTemplate(t);
                setSubject(t.subject || t.name || '');
                setBody(t.body || '');
                setOriginalBody(t.body || '');
                setActive(!!t.active);
                setAdminConfirmed(false);
            })
            .catch((err) => {
                if (!cancelled) setLoadError(err?.message || 'Failed to load template.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, source, id, token]);

    const previewBody = useMemo(() => {
        if (!template) return '';
        return applySampleMergeValues(body, template.sampleMergeValues || {});
    }, [body, template]);

    const insertVar = (tokenVar: string) => {
        const ta = document.getElementById('msg-tpl-body') as HTMLTextAreaElement | null;
        if (!ta) {
            setBody((prev) => prev + tokenVar);
            return;
        }
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const next = body.slice(0, start) + tokenVar + body.slice(end);
        setBody(next);
    };

    const handleSave = async () => {
        if (!template || !token) return;
        const err = validateMessageTemplateForm({
            source: template.source,
            subject,
            body,
            originalBody,
            restricted: !!template.restricted,
            errorCode: !!template.errorCode,
            adminConfirmed,
        });
        if (err) {
            Swal.fire({ icon: 'warning', title: 'Cannot save', text: err });
            return;
        }

        setSaving(true);
        try {
            const res = await apiClient('/MessageTemplates/save', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                }),
                body: JSON.stringify({
                    source: template.source,
                    id: template.id,
                    templateName: template.name,
                    subject,
                    body,
                    originalBody,
                    active,
                    adminConfirmed: template.restricted ? adminConfirmed : true,
                }),
            });
            if (res?.status) {
                await Swal.fire({ icon: 'success', title: 'Saved', text: res.message || 'Template updated.' });
                onSaved();
                onClose();
            } else {
                Swal.fire({ icon: 'error', title: 'Save failed', text: res?.message || 'Unable to save.' });
            }
        } catch (e: unknown) {
            Swal.fire({
                icon: 'error',
                title: 'Save failed',
                text: e instanceof Error ? e.message : 'Unable to save.',
            });
        } finally {
            setSaving(false);
        }
    };

    const showRestrictWarn = !!template?.restricted;
    const isEmail = template?.source === 'email';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            PaperProps={{ sx: editDialogPaperSx }}
        >
            {loading && <LinearProgress />}
            <Box sx={editDialogHeaderSx}>
                <Box sx={editDialogHeaderIconSx}>
                    <MailOutlineIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 500 }}>
                    {template ? `Edit: ${template.name}` : 'Edit template'}
                </Typography>
                <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="Close" size="small">
                    <CloseOutlinedIcon />
                </IconButton>
            </Box>

            <Box sx={editDialogBodySx}>
                {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
                {template && (
                    <>
                        {showRestrictWarn && (
                            <Box sx={{ mb: 2 }}>
                                <Alert severity="warning" sx={{ mb: 1 }}>
                                    {template.restrictMessage
                                        || 'This template is restricted. Confirm with dev before saving.'}
                                </Alert>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={adminConfirmed}
                                            onChange={(e) => setAdminConfirmed(e.target.checked)}
                                        />
                                    }
                                    label="I confirm this change has been reviewed with dev"
                                />
                            </Box>
                        )}

                        <Box sx={sectionSx}>
                            <SectionLabel>Template details</SectionLabel>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    gap: 2,
                                    mb: 2,
                                }}
                            >
                                <Box>
                                    <FieldLabel htmlFor="msg-tpl-key" readOnly>
                                        Template key
                                    </FieldLabel>
                                    <TextField
                                        id="msg-tpl-key"
                                        fullWidth
                                        size="small"
                                        value={template.templateNameKey}
                                        InputProps={{ readOnly: true }}
                                        sx={readOnlyInputSx}
                                    />
                                </Box>
                                <Box>
                                    <FieldLabel htmlFor="msg-tpl-display-name" readOnly>
                                        Display name
                                    </FieldLabel>
                                    <TextField
                                        id="msg-tpl-display-name"
                                        fullWidth
                                        size="small"
                                        value={template.name}
                                        InputProps={{ readOnly: true }}
                                        sx={readOnlyInputSx}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ maxWidth: { sm: '50%' }, mb: 2, pr: { sm: 1 } }}>
                                <FieldLabel htmlFor="msg-tpl-status">Status</FieldLabel>
                                <FormControl fullWidth size="small">
                                    <Select
                                        id="msg-tpl-status"
                                        value={active ? 'Active' : 'Inactive'}
                                        onChange={(e) => setActive(e.target.value === 'Active')}
                                        sx={inputSx}
                                    >
                                        <MenuItem value="Active">Active</MenuItem>
                                        <MenuItem value="Inactive">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            {isEmail && (
                                <Box>
                                    <FieldLabel htmlFor="msg-tpl-subject">Email subject</FieldLabel>
                                    <TextField
                                        id="msg-tpl-subject"
                                        fullWidth
                                        size="small"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        sx={inputSx}
                                    />
                                </Box>
                            )}
                        </Box>

                        {(template.mergeVars || []).length > 0 && (
                            <Box sx={sectionSx}>
                                <SectionLabel>Available variables — click to insert at cursor</SectionLabel>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.625 }}>
                                    {(template.mergeVars || []).map((v) => (
                                        <Button
                                            key={v}
                                            size="small"
                                            variant="outlined"
                                            onClick={() => insertVar(v)}
                                            sx={varChipSx}
                                        >
                                            {v}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Box sx={sectionSx}>
                            <SectionLabel>{isEmail ? 'Email body' : 'Message body'}</SectionLabel>
                            <TextField
                                id="msg-tpl-body"
                                fullWidth
                                multiline
                                minRows={8}
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                sx={bodyTextareaSx}
                            />
                        </Box>

                        <Box sx={{ mb: 0 }}>
                            <SectionLabel>Live preview — sample data substituted</SectionLabel>
                            <MessageTemplateBodyPreview
                                content={previewBody}
                                emptyLabel="(empty)"
                                sx={previewBoxSx}
                            />
                        </Box>
                    </>
                )}
            </Box>

            <Box sx={editDialogFooterSx}>
                <Button variant="outlined" onClick={onClose} disabled={saving} sx={cancelButtonSx}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleSave}
                    disabled={loading || saving || !template}
                    sx={saveButtonSx}
                >
                    {saving ? 'Saving…' : 'Save template'}
                </Button>
            </Box>
        </Dialog>
    );
};

function formatUpdated(
    dateModified: string | null,
    _modifiedBy: number | null,
    modifiedByName?: string | null,
): string {
    const datePart = dateModified && moment(dateModified).isValid()
        ? moment(dateModified).format('MMM D, YYYY')
        : '—';
    const displayName = modifiedByName?.trim();
    const by = displayName || 'DevAdmin Staff';
    return `Updated ${datePart} · ${by}`;
}

export { formatUpdated };
